import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable, of } from 'rxjs';
import { catchError, map } from 'rxjs/operators';
import { environment } from '../../environments/environment';
import { TokenService } from './token.service';

/**
 * Thrown when auth initiates a redirect to the identity provider (e.g. Microsoft).
 * Callers should show loading and NOT navigate to not-authorized, since auth has not yet completed.
 */
export class RedirectingToLoginError extends Error {
  constructor() {
    super('Redirecting to sign in');
    this.name = 'RedirectingToLoginError';
    Object.setPrototypeOf(this, RedirectingToLoginError.prototype);
  }
}

/**
 * Custom claim name for tenant/organization IDs the user has access to (multi-value claim, array).
 * Read from the access token (issued for the OFAC API); fallback to ID token if missing.
 * Must match the claim name in Azure (Token configuration / optional claims).
 */
export const ORGANIZATION_ID_CLAIM = 'user.organizationid';

@Injectable({
  providedIn: 'root'
})
export class AuthService {
  private isAuthenticated = false;
  private userRoles: string[] = [];
  private organizationIds: string[] = [];

  constructor(
    private http: HttpClient,
    private tokenService: TokenService
  ) {}

  async ensureAuthenticatedAsync(): Promise<void> {
    if (!this.isAuthenticated) {
      if (environment.useMockAuth) {
        this.isAuthenticated = true;
        this.userRoles = environment.mockAuth.roles || [];
      } else if (this.tokenService.hasValidToken()) {
        await this.loadUserFromToken();
      } else {
        await this.loginAsync();
      }
    }
  }

  /**
   * Initiates the OAuth 2.0 login flow
   */
  async loginAsync(): Promise<void> {
    if (environment.useMockAuth) {
      this.isAuthenticated = true;
      this.userRoles = environment.mockAuth.roles || [];
      return;
    }

    if (!environment.auth?.redirectUrl) {
      throw new Error('redirectUrl is not configured in environment.auth');
    }

    const codeVerifier = this.generateCodeVerifier();
    const codeChallenge = await this.generateCodeChallenge(codeVerifier);
    const state = this.generateState();

    sessionStorage.setItem('pkce_code_verifier', codeVerifier);
    sessionStorage.setItem('oauth_state', state);

    const authUrl = this.buildAuthorizationUrl(codeChallenge, state);
    if (!authUrl.includes('redirect_uri=')) {
      throw new Error('Failed to build authorization URL: redirect_uri is missing');
    }

    window.location.href = authUrl;
    throw new RedirectingToLoginError();
  }

  /**
   * Handles the OAuth callback and exchanges code for token
   */
  async handleCallbackAsync(code: string, state: string): Promise<void> {
    if (environment.useMockAuth) {
      this.isAuthenticated = true;
      this.userRoles = environment.mockAuth.roles || [];
      return;
    }

    // Verify state
    const storedState = sessionStorage.getItem('oauth_state');
    if (!storedState || storedState !== state) {
      throw new Error('Invalid state parameter');
    }

    // Get code verifier
    const codeVerifier = sessionStorage.getItem('pkce_code_verifier');
    if (!codeVerifier) {
      throw new Error('Code verifier not found');
    }

    // Exchange code for token
    await this.exchangeCodeForToken(code, codeVerifier);

    // Clean up
    sessionStorage.removeItem('oauth_state');
    sessionStorage.removeItem('pkce_code_verifier');

    // Load user info from token
    await this.loadUserFromToken();
  }

  /**
   * Loads user information from the stored token
   */
  private async loadUserFromToken(): Promise<void> {
    const claims = this.tokenService.getTokenClaims();
    if (!claims) {
      throw new Error('No token claims found');
    }

    // Extract roles from access token (same token sent to the API)
    // Azure AD app roles appear in 'roles' claim
    this.userRoles = [];
    if (claims.roles && Array.isArray(claims.roles)) {
      this.userRoles = claims.roles;
    } else if (claims['http://schemas.microsoft.com/ws/2008/06/identity/claims/role']) {
      const roleClaim = claims['http://schemas.microsoft.com/ws/2008/06/identity/claims/role'];
      this.userRoles = Array.isArray(roleClaim) ? roleClaim : [roleClaim];
    }

    // Extract OrganizationId (multi-value claim, array); try access token first, then ID token fallback
    this.organizationIds = this.extractOrganizationIdsFromClaims(claims);
    if (this.organizationIds.length === 0) {
      const idClaims = this.tokenService.getIdTokenClaims();
      if (idClaims) {
        this.organizationIds = this.extractOrganizationIdsFromClaims(idClaims);
      }
    }

    this.isAuthenticated = true;
  }

  /**
   * Extracts organization IDs from claims. The claim is a multi-value claim (array of strings).
   * Also supports stringified JSON array or single string for backward compatibility.
   */
  private extractOrganizationIdsFromClaims(claims: Record<string, unknown>): string[] {
    const raw = claims[ORGANIZATION_ID_CLAIM];
    if (raw === undefined || raw === null) {
      return [];
    }
    if (Array.isArray(raw)) {
      const ids = raw
        .filter((v: unknown) => typeof v === 'string')
        .map((v: string) => String(v).trim())
        .filter(Boolean);
      return ids;
    }
    if (typeof raw === 'string' && raw.trim()) {
      try {
        const parsed = JSON.parse(raw) as unknown;
        if (Array.isArray(parsed)) {
          return parsed
            .filter((v: unknown) => typeof v === 'string')
            .map((v: string) => String(v).trim())
            .filter(Boolean);
        }
      } catch {
        // Not JSON; treat as single value
      }
      return [raw.trim()];
    }
    return [];
  }

  /**
   * Exchanges authorization code for access token
   */
  private async exchangeCodeForToken(code: string, codeVerifier: string): Promise<void> {
    // Azure AD token endpoint is at /token (same base URL as authorize)
    const tokenUrl = environment.auth.authorityUrl.replace('/authorize', '/token');
    
    const body = new URLSearchParams();
    body.set('grant_type', 'authorization_code');
    body.set('client_id', environment.auth.applicationId);
    body.set('code', code);
    body.set('redirect_uri', environment.auth.redirectUrl);
    body.set('code_verifier', codeVerifier);
    // Include scopes in token request
    const scopes = [
      environment.ofacService.scope
    ].join(' ');
    body.set('scope', scopes);

    try {
      const response = await fetch(tokenUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded'
        },
        body: body.toString()
      });

      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(`Token exchange failed: ${response.status} ${errorText}`);
      }

      const tokenResponse = await response.json();
      
      // Store tokens
      this.tokenService.setAccessToken(tokenResponse.access_token, tokenResponse.expires_in || 3600);
      if (tokenResponse.id_token) {
        this.tokenService.setIdToken(tokenResponse.id_token);
      }
    } catch (error) {
      throw error;
    }
  }

  /**
   * Builds the authorization URL
   */
  private buildAuthorizationUrl(codeChallenge: string, state: string): string {
    // Validate redirect URI is configured
    if (!environment.auth.redirectUrl) {
      throw new Error('redirectUrl is not configured in environment.auth');
    }

    // Combine required scopes
    const scopes = [
      'openid',
      'profile',
      'email',
      environment.ofacService.scope
    ].join(' ');

    const redirectUri = encodeURIComponent(environment.auth.redirectUrl);
    const params = [
      `client_id=${encodeURIComponent(environment.auth.applicationId)}`,
      `response_type=code`,
      `redirect_uri=${redirectUri}`,
      `response_mode=query`,
      `scope=${encodeURIComponent(scopes)}`,
      `state=${encodeURIComponent(state)}`,
      `code_challenge=${encodeURIComponent(codeChallenge)}`,
      `code_challenge_method=S256`
    ].join('&');

    const fullUrl = `${environment.auth.authorityUrl}?${params}`;
    if (!fullUrl.includes('redirect_uri=')) {
      throw new Error('redirect_uri parameter is missing from authorization URL');
    }

    return fullUrl;
  }

  /**
   * Generates a cryptographically random code verifier
   */
  private generateCodeVerifier(): string {
    const array = new Uint8Array(32);
    crypto.getRandomValues(array);
    return btoa(String.fromCharCode(...array))
      .replace(/\+/g, '-')
      .replace(/\//g, '_')
      .replace(/=/g, '');
  }

  /**
   * Generates a code challenge from the verifier using SHA256
   */
  private async generateCodeChallenge(verifier: string): Promise<string> {
    const encoder = new TextEncoder();
    const data = encoder.encode(verifier);
    const digest = await crypto.subtle.digest('SHA-256', data);
    const array = new Uint8Array(digest);
    return btoa(String.fromCharCode(...array))
      .replace(/\+/g, '-')
      .replace(/\//g, '_')
      .replace(/=/g, '');
  }

  /**
   * Generates a random state parameter
   */
  private generateState(): string {
    const array = new Uint8Array(16);
    crypto.getRandomValues(array);
    return btoa(String.fromCharCode(...array))
      .replace(/\+/g, '-')
      .replace(/\//g, '_')
      .replace(/=/g, '');
  }

  /**
   * Logs out the user
   */
  logout(): void {
    this.isAuthenticated = false;
    this.userRoles = [];
    this.organizationIds = [];
    this.tokenService.clearTokens();
    
    if (!environment.useMockAuth) {
      // Redirect to Azure AD logout
      const logoutUrl = environment.auth.authorityUrl.replace('/authorize', '/logout');
      const params = new URLSearchParams({
        post_logout_redirect_uri: window.location.origin
      });
      window.location.href = `${logoutUrl}?${params.toString()}`;
    }
  }

  /**
   * Gets all roles for the current user
   */
  getUserRoles(): string[] {
    return [...this.userRoles];
  }

  /**
   * Checks if the user has a specific role
   */
  hasRole(role: string): boolean {
    return this.userRoles.includes(role);
  }

  /**
   * True if the user has the data read role (OfacMaint.Data.Read).
   */
  hasDataReadRole(): boolean {
    return this.hasRole('OfacMaint.Data.Read');
  }

  /**
   * True if the user has the data write role (OfacMaint.Data.ReadWrite).
   * Accepts both casings for compatibility with different token issuers.
   */
  hasDataWriteRole(): boolean {
    return this.hasRole('OfacMaint.Data.ReadWrite') || this.hasRole('OfacMaint.Data.Readwrite');
  }

  /**
   * True if the user has access to the app (data read and/or data write role).
   */
  hasAccess(): boolean {
    return this.hasDataReadRole() || this.hasDataWriteRole();
  }

  /**
   * Checks if the user has any of the specified roles
   */
  hasAnyRole(...roles: string[]): boolean {
    return roles.some(role => this.userRoles.includes(role));
  }

  /**
   * Checks if the user has all of the specified roles
   */
  hasAllRoles(...roles: string[]): boolean {
    return roles.every(role => this.userRoles.includes(role));
  }

  /**
   * Returns tenant/organization IDs from the access token claim (OrganizationId).
   */
  getOrganizationIds(): string[] {
    if (environment.useMockAuth) {
      const mock = environment.mockAuth?.organizationIds;
      return Array.isArray(mock) ? [...mock] : [];
    }
    return [...this.organizationIds];
  }

  /**
   * True if the OrganizationId claim contains "Agvantis" (case insensitive).
   */
  async isAgVantisUserAsync(): Promise<boolean> {
    const ids = this.getOrganizationIds();
    return ids.some(id => String(id).toLowerCase() === 'agvantis');
  }

  /**
   * When user has exactly one tenant (and it is not Agvantis), returns that as sourceId.
   */
  async getSourceIdAsync(): Promise<string | null> {
    if (environment.useMockAuth) {
      return environment.mockAuth?.sourceId ?? null;
    }
    if (this.tokenService.hasValidToken() && this.organizationIds.length === 0) {
      await this.loadUserFromToken();
    }
    const ids = this.getOrganizationIds();
    if (ids.length === 1 && ids[0].toLowerCase() !== 'agvantis') {
      return ids[0];
    }
    return null;
  }

  getIsAuthenticated(): boolean {
    return this.isAuthenticated;
  }

  /**
   * Gets the current access token
   */
  getAccessToken(): string | null {
    return this.tokenService.getAccessToken();
  }
}

